import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { filter } from 'lodash';
import { queryCache } from 'react-query';
import { GTColumnSelectAnchor } from 'src/components/GenericTable';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { TableIds } from 'src/api/types/misc';
import { QKeys } from 'src/api/types';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TextOverflowTooltip, Tooltip } from 'src/common';
import { formatDate } from 'src/utils/misc';
import { useStateSelector } from 'src/redux';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import type { ExportReportTypeAnalytics } from 'src/utils/mixpanel/types';
import { useReportDownloadUrlMutation } from 'src/graphql/_generated';
import { showErrorToast, toastMessages } from 'src/components/Toast';
import { ReportRow } from './util';

type DeleteModalValue = {
  id: string | null;
  // Captured at open time so analytics fired after the mutation completes still
  // have row context — by then refetchQueries has dropped the row from `rows`.
  reportType?: string;
};

type UseColumns = (a: { data?: ReportRow[] }) => {
  columns: ColumnDef<ReportRow>[];
  GTColumnSelectAnchorExported: JSX.Element;
  deleteModal: DeleteModalValue;
  setDeleteModal: (a: DeleteModalValue) => void;
};

const defaultColumns = ['reportId', 'createdAt', 'object', 'format', 'requestedBy', 'status', 'access', 'expiresAt'];

const StatusText = styled.span<{ $variant: 'generating' | 'generated' | 'failed' | 'default' }>`
  color: ${({ theme, $variant }) => {
    if ($variant === 'generating') return theme.colors.statusGreen;
    if ($variant === 'generated') return theme.colors.prussianBlue;
    if ($variant === 'failed') return theme.colors.error;
    return theme.colors.davysGrey;
  }};
`;

const FailedWrap = styled.span`
  display: inline-flex;
  align-items: center;
  grid-gap: 6px;

  svg {
    flex-shrink: 0;
  }
`;

const FailedInfoIcon = styled.svg.attrs({
  width: 14,
  height: 14,
  viewBox: '0 0 20 20',
  xmlns: 'http://www.w3.org/2000/svg',
  children: (
    <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V9H11V15ZM11 7H9V5H11V7Z" />
  ),
})`
  fill: ${({ theme }) => theme.colors.error};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  grid-gap: 2px;
  padding-right: 8px;

  button {
    background: transparent;
    border: none;
    width: 30px;
    height: 30px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: ${({ theme }) => theme.colors.cultured};
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.4;

      &:hover {
        background-color: transparent;
      }
    }
  }
`;

// Disabled visual is handled by the parent button's `:disabled { opacity: 0.4 }`
// (see Actions styled component) — no need for a separate disabled fill.
const DownloadIcon: React.FC = () => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="18" viewBox="0 0 16 20" fill="none">
      <path
        d="M8 0C8.26522 0 8.51957 0.105357 8.70711 0.292893C8.89464 0.48043 9 0.734784 9 1V11.586L11.293 9.293C11.4816 9.11084 11.7342 9.01005 11.9964 9.01233C12.2586 9.0146 12.5094 9.11977 12.6948 9.30518C12.8802 9.49059 12.9854 9.7414 12.9877 10.0036C12.99 10.2658 12.8892 10.5184 12.707 10.707L8.707 14.707C8.51947 14.8945 8.26516 14.9998 8 14.9998C7.73484 14.9998 7.48053 14.8945 7.293 14.707L3.293 10.707C3.19749 10.6148 3.12131 10.5044 3.0689 10.3824C3.01649 10.2604 2.9889 10.1292 2.98775 9.9964C2.9866 9.86362 3.0119 9.73194 3.06218 9.60905C3.11246 9.48615 3.18671 9.3745 3.2806 9.28061C3.3745 9.18671 3.48615 9.11246 3.60905 9.06218C3.73194 9.0119 3.86362 8.9866 3.9964 8.98775C4.12918 8.9889 4.2604 9.01649 4.3824 9.0689C4.50441 9.12131 4.61475 9.19749 4.707 9.293L7 11.586V1C7 0.734784 7.10536 0.48043 7.29289 0.292893C7.48043 0.105357 7.73478 0 8 0ZM1 15C1.26522 15 1.51957 15.1054 1.70711 15.2929C1.89464 15.4804 2 15.7348 2 16V18H14V16C14 15.7348 14.1054 15.4804 14.2929 15.2929C14.4804 15.1054 14.7348 15 15 15C15.2652 15 15.5196 15.1054 15.7071 15.2929C15.8946 15.4804 16 15.7348 16 16V18C16 18.5304 15.7893 19.0391 15.4142 19.4142C15.0391 19.7893 14.5304 20 14 20H2C1.46957 20 0.960859 19.7893 0.585786 19.4142C0.210714 19.0391 0 18.5304 0 18V16C0 15.7348 0.105357 15.4804 0.292893 15.2929C0.48043 15.1054 0.734784 15 1 15Z"
        fill={colors.davysGrey}
      />
    </svg>
  );
};

const DeleteIcon: React.FC = () => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="6.5 6 16 18" fill="none">
      <path
        d="M7.32494 11.0964C6.92455 11.0964 6.64677 10.7203 6.79701 10.3755C7.33024 9.15097 8.62375 8.28458 10.1315 8.28458H10.8282C11.0882 7.00979 12.2916 6 13.7645 6H14.9755C16.4473 6 17.6515 7.00908 17.9118 8.28458H18.6085C20.1159 8.28458 21.4098 9.15097 21.943 10.3755C22.0932 10.7203 21.8155 11.0964 21.4147 11.0964H7.32494ZM14.9755 7.05442H13.7645C12.9357 7.05442 12.2322 7.57214 11.9953 8.28458H16.7447C16.5078 7.57214 15.8043 7.05442 14.9755 7.05442Z"
        fill={colors.statusRed}
      />
      <path
        d="M19.8604 22.5318C19.7972 23.3556 19.0509 24.0009 18.1616 24.0009H10.5882C9.69886 24.0009 8.95258 23.3556 8.88938 22.5318L8.09427 12.1562H20.6562L19.8604 22.5318ZM12.8229 14.9768C12.8074 14.6862 12.5402 14.4619 12.2276 14.4767C11.9146 14.4911 11.6732 14.7389 11.6891 15.0296L11.9918 20.6531C12.007 20.935 12.2579 21.154 12.5584 21.154C12.8853 21.154 13.1419 20.9002 13.1256 20.6004L12.8229 14.9768ZM16.5229 14.4767C16.2103 14.4623 15.9431 14.6862 15.9276 14.9768L15.6249 20.6004C15.6086 20.9002 15.8656 21.154 16.1922 21.154C16.4926 21.154 16.7435 20.935 16.7587 20.6531L17.0614 15.0296C17.077 14.7389 16.8359 14.4911 16.5229 14.4767Z"
        fill={colors.statusRed}
      />
    </svg>
  );
};

const renderStatus = (params: { rowId: string; status: string; errorMessage?: string | null }) => {
  const { rowId, status, errorMessage } = params;

  const s = (status || '').toLowerCase();
  if (s === 'pending' || s === 'processing') {
    return <StatusText $variant="generating">Generating</StatusText>;
  }
  if (s === 'completed') {
    return <StatusText $variant="generated">Generated</StatusText>;
  }
  if (s === 'failed') {
    if (!errorMessage) return <StatusText $variant="failed">Failed</StatusText>;
    return (
      <Tooltip dataId={`report-status-${rowId}`} dataHtml={errorMessage} place="top" noIcon inline>
        <FailedWrap>
          <StatusText $variant="failed">Failed</StatusText>
          <FailedInfoIcon />
        </FailedWrap>
      </Tooltip>
    );
  }
  if (s === 'canceled') {
    return <StatusText $variant="default">Canceled</StatusText>;
  }
  return <StatusText $variant="default">{status || '-'}</StatusText>;
};

export const useColumns: UseColumns = ({ data = [] }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${TableIds.reportCentre}-columns`, defaultColumns);
  const [deleteModal, setDeleteModal] = useState<DeleteModalValue>({ id: null });

  // BE removed `downloadUrl` from ExportJob — every download must now go through
  // this mutation so the BE can log an audit entry. Returned URL is valid 15 min.
  const [requestDownloadUrl, { loading: downloading }] = useReportDownloadUrlMutation();

  const handleDownload = async (jobId: string, reportTypeForAnalytics: ExportReportTypeAnalytics) => {
    try {
      const result = await requestDownloadUrl({ variables: { jobId } });
      const url = result.data?.reportDownloadUrl;
      if (!url) {
        showErrorToast(toastMessages.export.downloadFailed.title);
        return;
      }
      // BE writes an audit row on every reportDownloadUrl call — invalidate
      // so the AUDIT section refetches and the new row shows up immediately.
      queryCache.invalidateQueries([QKeys.Audit]);
      mpEvent(MPEvents.ExportDownload, { reportType: reportTypeForAnalytics, jobId });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const serverMsg = (err as { graphQLErrors?: { message: string }[] })?.graphQLErrors?.[0]?.message;
      showErrorToast(serverMsg || toastMessages.export.downloadFailed.title);
    }
  };

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.tdm];

    const list: ColumnDef<ReportRow>[] = [
      {
        id: 'reportId',
        header: 'Object Name',
        accessorFn: (row) => `${row.objectLabel} Report`,
        cell: (info) => <TextOverflowTooltip>{(info.getValue() as string) || '-'}</TextOverflowTooltip>,
        sortingFn: sortingFns.text,
      },
      {
        id: 'createdAt',
        header: 'Created on',
        accessorFn: (row) => row.createdAt,
        cell: ({ row }) => formatDate(row.original.createdAt, { formatType: 'datetime' }) || '-',
      },
      {
        id: 'object',
        header: 'Object',
        accessorFn: (row) => row.objectLabel,
        cell: (info) => (info.getValue() as string) || '-',
        sortingFn: sortingFns.text,
      },
      {
        id: 'format',
        header: 'Format',
        accessorFn: (row) => row.formatLabel,
        cell: (info) => (info.getValue() as string) || '-',
        sortingFn: sortingFns.text,
      },
      {
        id: 'requestedBy',
        header: 'Created by',
        // requestedBy is now a User object ({ id, name, email }); we sort on the
        // displayable label (name → email → '-'), not the object.
        accessorFn: (row) => row.requestedBy?.name || row.requestedBy?.email || '',
        cell: (info) => <TextOverflowTooltip>{(info.getValue() as string) || '-'}</TextOverflowTooltip>,
        sortingFn: sortingFns.text,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => row.status,
        cell: ({ row }) =>
          renderStatus({
            rowId: row.original.id,
            status: row.original.status,
            errorMessage: row.original.errorMessage,
          }),
        sortingFn: sortingFns.text,
      },
      {
        id: 'access',
        header: 'Access',
        // BE returns the ExportAccess enum lowercase ('user' | 'workspace').
        // Map to FE-facing labels that mirror the ExportAccessToggle copy
        // ("Make this report public for the entire workspace?").
        accessorFn: (row) => {
          const v = (row.access || '').toLowerCase();
          if (v === 'workspace') return 'Workspace';
          if (v === 'user') return 'Private';
          return '-';
        },
        cell: (info) => (info.getValue() as string) || '-',
        sortingFn: sortingFns.text,
      },
      {
        id: 'expiresAt',
        header: () => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            Expiry
            <Tooltip
              dataId="rc-expiry-header"
              dataHtml="Reports are retained for one year from the date they were created."
              place="top"
              infoIconStyle={{ width: 14, height: 14 }}
            />
          </span>
        ),
        accessorFn: (row) => row.expiresAt,
        cell: ({ row }) => formatDate(row.original.expiresAt, { formatType: 'date' }) || '-',
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        size: 84,
        maxSize: 84,
        enableSorting: false,
        cell: ({ row }) => {
          const { status, id, reportType } = row.original;
          const canDownload = (status || '').toLowerCase() === 'completed';
          const reportTypeForAnalytics = (reportType || '').toUpperCase() as ExportReportTypeAnalytics;
          return (
            <Actions className="ThreeDots__Wraper">
              <button
                type="button"
                disabled={!canDownload || downloading}
                onClick={(e) => {
                  e.stopPropagation();
                  if (canDownload && !downloading) {
                    handleDownload(id, reportTypeForAnalytics);
                  }
                }}
                aria-label="Download"
                data-cy="report-download-btn"
              >
                <DownloadIcon />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteModal({ id, reportType });
                }}
                aria-label="Delete"
                data-cy="report-delete-btn"
              >
                <DeleteIcon />
              </button>
            </Actions>
          );
        },
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
    // `downloading` toggles the download button's disabled state; without it
    // the memo wouldn't recompute and the button would stay enabled.
  }, [data, selectedColumns, downloading]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.reportCentre}
      options={[
        { key: 'reportId', title: 'Object Name' },
        { key: 'createdAt', title: 'Created on' },
        { key: 'object', title: 'Object' },
        { key: 'format', title: 'Format' },
        { key: 'requestedBy', title: 'Created by' },
        { key: 'status', title: 'Status' },
        { key: 'access', title: 'Access' },
        { key: 'expiresAt', title: 'Expiry' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return {
    columns,
    GTColumnSelectAnchorExported,
    deleteModal,
    setDeleteModal,
  };
};
